/**
 * "How do we know we are getting the right data?"
 *
 * The board is a join across two Airtable bases whose spellings do not have to
 * agree, so this panel reports what each side actually contains and, crucially,
 * where they DISAGREE — a unit only one of them knows about. That is the case
 * that used to be invisible: a role whose unit the people database has never
 * heard of simply vanished into a bucket at the bottom of the board.
 *
 * Collapsed by default. It is a diagnostic, not part of the daily job.
 */
import { useState } from 'react';
import { AlertTriangle, ChevronRight, Database } from 'lucide-react';
import type { DemandDiagnostics } from '../../../types/taDemand';

export function DataSources({ d }: { d: DemandDiagnostics }) {
  const [open, setOpen] = useState(false);
  const mismatches = d.onlyInHdb.length + d.onlyInPeopleDb.length;

  return (
    <div className="rounded-md border border-gray-200 bg-white text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-gray-600 hover:bg-gray-50"
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <Database className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <span className="font-semibold text-gray-700">Where this data comes from</span>
        <span className="text-gray-500">
          {d.people.available
            ? `${d.people.employees} people · ${d.people.units.length} units`
            : 'people database not connected'}
          {' · '}
          {d.hdb.roles} roles in the HDB
        </span>
        {mismatches > 0 && (
          <span className="ml-auto flex shrink-0 items-center gap-1 rounded-sm bg-amber-50 px-1.5 font-semibold text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            {mismatches} unit{mismatches === 1 ? '' : 's'} only in one source
          </span>
        )}
      </button>

      {open && (
        <div className="grid gap-4 border-t border-gray-100 px-3 py-3 sm:grid-cols-2">
          <Column
            title="People database (Personio mirror)"
            sub={
              d.people.activeColumn
                ? `Structure and current team. Who is still here is decided by the "${d.people.activeColumn}" column — ${d.people.excluded} excluded, ${d.people.external ?? 0} external, ${d.people.onLeave ?? 0} on leave${
                    d.people.missingHeadcount
                      ? `, ${d.people.missingHeadcount} with no value there (judged on their status text instead)`
                      : ''
                  }${
                    d.people.contractEnded
                      ? `. ${d.people.contractEnded} more are left off because their fixed-term contract has already run out`
                      : ''
                  }.`
                : d.people.statusColumn
                  ? `Structure and current team. Decided by the "${d.people.statusColumn}" text — ${d.people.excluded} excluded.`
                  : 'Structure and current team. ⚠ No headcount or status column resolved, so NOBODY is filtered out — leavers will be listed.'
            }
            rows={d.people.units}
            empty={
              d.people.available
                ? 'No units found — check the Unit column resolved.'
                : 'Not connected.'
            }
            warn={
              d.people.missingUnit > 0
                ? `${d.people.missingUnit} people have no unit set`
                : d.people.missingDepartment > 0
                  ? `${d.people.missingDepartment} people have no department set`
                  : null
            }
          />
          <Column
            title="Hiring database"
            sub="Provides the roles. Matched onto the structure above."
            rows={d.hdb.units}
            empty="No roles found."
            warn={null}
          />

          {/*
            WHICH COLUMN, AND WHAT IT CONTAINS.
            "where does this information come from? please double check
            correctness" has no answer a lead can act on unless the board states
            it. Every value the headcount column holds is listed with its count:
            a value that ought to mean "gone" and is not being excluded shows up
            here as a number, which is the only way somebody other than the
            author can spot it.
          */}
          <div className="min-w-0 sm:col-span-2">
            <div className="font-semibold text-gray-800">Which column each value came from</div>
            <p className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-gray-600">
              <span>
                Still here: <b>{d.people.activeColumn ?? '—'}</b>
              </span>
              <span>
                Status: <b>{d.people.statusColumn ?? '—'}</b>
              </span>
              <span>
                IC level: <b>{d.people.levelColumn ?? '— none found'}</b>
                {d.people.missingLevel ? (
                  <span className="text-amber-700"> · {d.people.missingLevel} blank upstream</span>
                ) : null}
              </span>
              <span>
                FTE: <b>{d.people.fteColumn ?? '— none found, everyone reads as 1.0'}</b>
                {d.people.missingFte ? (
                  <span className="text-amber-700"> · {d.people.missingFte} blank upstream</span>
                ) : null}
              </span>
            </p>
          </div>

          {(d.people.headcountCounts?.length ?? 0) > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <div className="font-semibold text-gray-800">
                {d.people.activeColumn}, as the people database writes it
              </div>
              <p className="mt-0.5 text-gray-500">
                Only <code>Active Headcount (FINN)</code> counts as internal headcount.{' '}
                <code>(Workmotion)</code> is external and <code>Parental Leave</code> is inactive —
                both are shown and marked but left out of the FTE. Every other value means the
                person is no longer on the team.
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600">
                {d.people.headcountCounts?.map((s) => (
                  <span key={s.name}>
                    {s.name} <span className="tabular-nums text-gray-400">{s.count}</span>
                  </span>
                ))}
              </p>
            </div>
          )}

          {d.people.statusCounts.length > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <div className="font-semibold text-gray-800">
                Employment status, as the people database writes it
              </div>
              <p className="mt-0.5 text-gray-500">
                Read only where the headcount column is empty.
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600">
                {d.people.statusCounts.map((s) => (
                  <span key={s.name}>
                    {s.name} <span className="tabular-nums text-gray-400">{s.count}</span>
                  </span>
                ))}
              </p>
            </div>
          )}

          {/*
            ⚠️ THE FIRST THING TO READ WHEN THE ROSTER LOOKS WRONG. Every value
            in the headcount column that named no live state: each is either a
            real leaver being correctly excluded, or a live state this code has
            never seen — and only somebody who can open the base can tell which.
          */}
          {d.people.vocabularyFailed && (
            <div className="min-w-0 rounded-md bg-rose-50 px-2 py-1.5 text-rose-800 sm:col-span-2">
              <b>The &ldquo;{d.people.activeColumn}&rdquo; column is not the one this expects.</b>{' '}
              Nothing in it matched a known value, so everyone is being judged on their status text
              instead. The values it holds are listed above — send them over and the vocabulary can
              be corrected.
            </div>
          )}
          {(d.people.unknownStanding?.length ?? 0) > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <div className="font-semibold text-gray-800">
                Values that mean &ldquo;not on the team&rdquo; — check this list
              </div>
              <p className="mt-0.5 text-gray-500">
                Each of these excluded somebody. A real leaver belongs here; a live state that is
                just worded differently does not, and the people it covers are missing from the
                board.
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600">
                {d.people.unknownStanding?.map((s) => (
                  <span key={s.name}>
                    {s.name} <span className="tabular-nums text-gray-400">{s.count}</span>
                  </span>
                ))}
              </p>
            </div>
          )}

          {/*
            WHERE THE TEAM CAME FROM. The org design workbook is the authority on
            which team somebody is in and the people database is the authority on
            who exists, so the two disagreeing is maintenance rather than an
            error — and a number is the only way anyone knows it needs doing.
          */}
          {(d.teamSources?.length ?? 0) > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <div className="font-semibold text-gray-800">How each colleague got their team</div>
              <p className="mt-0.5 text-gray-500">
                <b>org-design</b> is the per-department tab of the org design workbook, which names
                everybody under their team — regenerate it with{' '}
                <code className="text-[11px]">extractOrgTeams.mjs</code> after a reorg.{' '}
                <b>title</b> is inferred from the job title and is a guess. <b>none</b> is somebody
                waiting to be placed by hand.
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600">
                {d.teamSources?.map((s) => (
                  <span key={s.name}>
                    {s.name} <span className="tabular-nums text-gray-400">{s.count}</span>
                  </span>
                ))}
              </p>
              {(d.orgDesign?.onlyInPeopleDbCount ?? 0) > 0 && (
                <p className="mt-1 text-gray-500">
                  {d.orgDesign?.onlyInPeopleDbCount} employed colleagues are not in the org design
                  yet — new joiners, usually, and they get no team from it:{' '}
                  <span className="text-gray-600">
                    {d.orgDesign?.onlyInPeopleDb.slice(0, 12).join(', ')}
                    {(d.orgDesign?.onlyInPeopleDbCount ?? 0) > 12 ? ' …' : ''}
                  </span>
                </p>
              )}
            </div>
          )}

          {/*
            "How can I help map this?" — the exact titles, as the people database
            writes them, that neither the job-family map nor the chart's own team
            names could place. Each is either a line for the map or a person to
            drag, and nobody can say which without seeing the list.
          */}
          {(d.unplacedTitles?.length ?? 0) > 0 && (
            <details className="min-w-0 sm:col-span-2">
              <summary className="cursor-pointer font-semibold text-gray-800">
                Job titles no team could claim (
                {d.unplacedTitles?.reduce((n, u) => n + u.people, 0)} people)
              </summary>
              <p className="mt-0.5 text-gray-500">
                Drag one of these people into a team and they stay there. A title several people
                share is worth a line in the map instead — send the unit and the title.
              </p>
              <div className="mt-1 space-y-1">
                {d.unplacedTitles?.map((u) => (
                  <div key={u.unit} className="min-w-0">
                    <span className="font-medium text-gray-700">{u.unit}</span>
                    <span className="ml-1 tabular-nums text-gray-400">{u.people}</span>
                    <span className="ml-2 text-gray-600">
                      {u.titles.map((t) => `${t.name}${t.count > 1 ? ` ×${t.count}` : ''}`).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {d.onlyInHdb.length > 0 && (
            <Mismatch
              title="Units the HDB has that the people database does not"
              hint="A brand-new team with a vacancy and nobody in it yet — or a spelling the two systems disagree on."
              names={d.onlyInHdb}
            />
          )}
          {d.onlyInPeopleDb.length > 0 && (
            <Mismatch
              title="Units with people but no roles in the HDB"
              hint="Normal for a team that is not hiring. Only worth checking if you expected a vacancy there."
              names={d.onlyInPeopleDb}
            />
          )}

          {(d.people.columns?.length ?? 0) > 0 && (
            <details className="min-w-0 sm:col-span-2">
              <summary className="cursor-pointer font-semibold text-gray-800">
                Every column the people database has ({d.people.columns?.length})
              </summary>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500">
                {d.people.columns?.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Column({
  title,
  sub,
  rows,
  empty,
  warn,
}: {
  title: string;
  sub: string;
  rows: { name: string; count: number }[];
  empty: string;
  warn: string | null;
}) {
  return (
    <div className="min-w-0">
      <div className="font-semibold text-gray-800">{title}</div>
      <p className="mt-0.5 text-gray-500">{sub}</p>
      {warn && <p className="mt-1 font-semibold text-amber-700">{warn}</p>}
      {rows.length === 0 ? (
        <p className="mt-2 text-gray-400">{empty}</p>
      ) : (
        <ul className="mt-2 max-h-48 overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.name} className="flex justify-between gap-2 py-0.5 text-gray-600">
              <span className="truncate">{r.name}</span>
              <span className="shrink-0 tabular-nums text-gray-400">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Mismatch({ title, hint, names }: { title: string; hint: string; names: string[] }) {
  return (
    <div className="min-w-0 rounded-sm bg-amber-50 p-2">
      <div className="font-semibold text-amber-900">{title}</div>
      <p className="mt-0.5 text-amber-800">{hint}</p>
      <p className="mt-1 break-words text-gray-700">{names.join(' · ')}</p>
    </div>
  );
}
